import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MatchLineUp from "./MatchLineUp";
import { getMatchById } from "../config/mainRequest";

const shortName = (name) =>
  (name || "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase() || "?";

// Popup that shows the match line up without navigating away from the current
// screen. Self-fetches the match by id (same shape as MatchDetails) so callers
// only need to pass matchId + token.
const LineupPopup = ({ open, onClose, matchId, token }) => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !matchId || !token) return;
    let cancelled = false;
    setLoading(true);
    setMatch(null);
    getMatchById({ match_id: matchId, token })
      .then((data) => {
        if (!cancelled) setMatch(data);
      })
      .catch((err) => console.error("[LineupPopup] match fetch failed:", err?.message))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, matchId, token]);

  const matchInfo = {
    id: matchId,
    pdfDetails: match?.pdf_details ?? null,
    status: match?.status ?? null,
    gameTime: match?.game_time ?? null,
    organizerId: match?.organizer_id ?? null,
    tournamentId:
      match?.tournament_details?.id ?? match?.tournament_id ?? match?.tournament?.id ?? null,
    startingLineupCount: match?.starting_lineup_count ?? 11,
    teamA: {
      id: match?.team_a_details?.id ?? null,
      name: match?.team_a_details?.name ?? "Team A",
      short: shortName(match?.team_a_details?.name) || "TA",
    },
    teamB: {
      id: match?.team_b_details?.id ?? null,
      name: match?.team_b_details?.name ?? "Team B",
      short: shortName(match?.team_b_details?.name) || "TB",
    },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ color: "var(--text-1)", fontSize: "18px", fontWeight: 600 }}>
          Match Line Up{matchId ? ` — #${matchId}` : ""}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "rgba(144, 152, 161, 0.7)",
            padding: "4px",
            "&:hover": { backgroundColor: "rgba(144, 152, 161, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: "var(--app-bg)" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 300, py: 6 }}>
            <CircularProgress size={32} sx={{ color: "rgba(232,108,0,1)" }} />
          </Stack>
        ) : (
          <Box>
            <MatchLineUp matchId={matchId} matchInfo={matchInfo} token={token} readOnly />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LineupPopup;
